import type { User } from 'firebase/auth';
import { useEffect, useRef } from 'react';
import { useConsent } from '@/contexts';
import { analyticsClient } from '@/lib';
import { settingsService } from '@/services';
import type { UserProfile } from '@/types';

export function useAnalytics(user: User | null, profile?: UserProfile | null) {
  const { analyticsAllowed } = useConsent();
  const identifiedForRef = useRef<string | null>(null);
  const lastPersistedConsentRef = useRef<{ uid: string; consent: boolean } | null>(null);

  // End the previous session and re-attach the pagehide listener whenever the
  // user or consent state changes. This must be declared before the identify
  // effect so the cleanup runs before the next identify + startSession.
  useEffect(() => {
    const handlePageHide = () => analyticsClient.endSession();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      analyticsClient.endSession();
    };
  }, [user, analyticsAllowed]);

  // Identify the user once their profile is available and consent is granted,
  // then immediately start the analytics session so session_start is captured
  // after the user has been identified.
  useEffect(() => {
    if (!user || !profile || analyticsAllowed !== true) return;

    if (identifiedForRef.current === user.uid) return;

    const success = analyticsClient.identify(user.uid, {
      ...(user.email ? { email: user.email } : {}),
      ...(profile.name ? { name: profile.name } : {}),
      ...(profile.birthday ? { birthday: profile.birthday } : {}),
      ...(profile.age ? { age: profile.age } : {}),
      ...(profile.gender?.length ? { gender: profile.gender } : {}),
      ...(profile.intentions?.length ? { intentions: profile.intentions } : {}),
      ...(profile.openTo?.length ? { open_to: profile.openTo } : {}),
      ...(profile.inviteCodeUsed ? { invite_code_used: profile.inviteCodeUsed } : {}),
      taking_a_break: profile.takingABreak,
      onboarded: profile.onboarded,
    });

    if (success) {
      identifiedForRef.current = user.uid;
      analyticsClient.startSession();
    }
  }, [user, profile, analyticsAllowed]);

  useEffect(() => {
    if (!user || analyticsAllowed === null) return;

    const last = lastPersistedConsentRef.current;
    if (last && last.uid === user.uid && last.consent === analyticsAllowed) return;

    lastPersistedConsentRef.current = { uid: user.uid, consent: analyticsAllowed };
    settingsService.updateSettings(user.uid, { analyticsConsent: analyticsAllowed }).catch(err => {
      console.warn('Failed to persist analytics consent:', err);
    });
  }, [user, analyticsAllowed]);
}
